import { stringify } from 'querystring'

import React from 'react'

import { Text } from '@wartek-id/text'
import { Icon } from '@wartek-id/icon'
import { Button } from '@wartek-id/button'

import { GATrackEvent } from 'utils/googleAnalytics'
import { Anchor } from 'components/Anchor'

import type { Topic } from 'types/microlearning'

const ContinueLearning = ({
  topicsData,
  progressData,
}: {
  topicsData: Topic[]
  progressData
}) => {
  let filteredProgress
  try {
    // first we remove all topics with finished aksinyata and progress without topic ids
    filteredProgress = progressData.data.filter((progress) =>
      topicsData
        .filter(
          (topic) =>
            topic?.badge?.state !== 'completed' &&
            topic?.badge?.state !== 'validated'
        )
        .map((topic) => topic.id)
        .includes(progress?.id)
    )

    if (filteredProgress.length === 0) {
      // bailout early if all of the topics are finished
      return null
    }

    // then we sort them out by the number of modules with finished post test
    filteredProgress = filteredProgress.sort((a, b) => {
      const aPostTestDoneCount =
        a.modules.filter((module) => module.postTest === true).length /
        a.modules.length
      const bPostTestDoneCount =
        b.modules.filter((module) => module.postTest === true).length /
        b.modules.length
      if (aPostTestDoneCount > bPostTestDoneCount) {
        return -1
      } else if (aPostTestDoneCount < bPostTestDoneCount) {
        return 1
      }
      return 0
    })

    // check if there user at least has one finished modules (post test === true)
    const topicWithFinishedPostTestModule = filteredProgress.filter(
      (progress) =>
        progress.modules.filter((module) => module.postTest === true).length > 0
    ).length

    if (topicWithFinishedPostTestModule === 0) {
      filteredProgress = filteredProgress.sort((a, b) => {
        const aMaterialDoneCount =
          a.modules.filter((module) => module.material === true).length /
          a.modules.length
        const bMaterialDoneCount =
          b.modules.filter((module) => module.material === true).length /
          b.modules.length
        if (aMaterialDoneCount > bMaterialDoneCount) {
          return -1
        } else if (aMaterialDoneCount < bMaterialDoneCount) {
          return 1
        }
        return 0
      })
    }

    filteredProgress = filteredProgress
      .map((progress) => {
        const currentTopic = topicsData.find(
          (topic) => topic.id === progress.id
        )
        return {
          image: currentTopic?.headerImage,
          title: currentTopic?.name,
          id: progress.id,
          percentage: progress.percentage,
          module: progress.modules
            .filter((module) => module.postTest === false)
            .sort((a, b) => {
              if (!!a.material && !b.material) {
                return -1
              } else if (!!b.material && !a.material) {
                return 1
              }
              return 0
            })
            .shift(),
        }
      })
      .shift()
  } catch (_) {
    filteredProgress = null
  }

  if (!!filteredProgress) {
    const destinationUrl = !filteredProgress.module
      ? `/${
          process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
          'pelatihan-mandiri'
        }/topik/${filteredProgress.id}`
      : `/${
          process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
          'pelatihan-mandiri'
        }/modul/${filteredProgress.module.moduleId}?${stringify({
          topik: filteredProgress.id,
          topik_name: filteredProgress.title,
        })}`

    return (
      <div
        data-testid="continue-learning"
        className="p-3 bg-white rounded border border-subdued mb-2"
      >
        <div className="flex justify-between mb-4 w-full">
          <div className="flex-auto mr-4">
            <Text variant="helper" color="subdued">
              {!!filteredProgress.module ? 'Modul' : 'Aksi Nyata'}
            </Text>
            <Text variant="heading-sm">{filteredProgress.title}</Text>
          </div>
          {!!filteredProgress.image ? (
            <img
              className="flex-grow-0 flex-shrink-0 w-[64px] h-[64px] rounded-sm object-cover object-center"
              src={filteredProgress.image}
              alt={filteredProgress.title}
            />
          ) : (
            <div className="flex items-center justify-center flex-grow-0 flex-shrink-0 w-[64px] h-[64px] bg-primary rounded-sm">
              <Icon color="inverse" fontSize="lg">
                menu_book
              </Icon>
            </div>
          )}
        </div>
        <div>
          <Anchor data-testid="link-target" href={destinationUrl}>
            <Button
              className="rounded"
              color="black"
              size="lg"
              fullWidth
              onClick={() => {
                GATrackEvent({
                  eventName: 'learning_activity',
                  eventParams: {
                    event_trigger: 'click_continue',
                    action_type: 'continue_sections',
                    meta: JSON.stringify({
                      destination_url: destinationUrl,
                    }),
                  },
                  sendDefaultParams: true,
                })
              }}
            >
              {!filteredProgress.module
                ? 'Kerjakan Aksi Nyata'
                : !filteredProgress.module.material
                ? 'Lanjut Pelajari'
                : 'Kerjakan Post Test'}
            </Button>
          </Anchor>
        </div>
      </div>
    )
  }

  return null
}

export default ContinueLearning
